import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, Filter, X, Eye, UserCheck, ArrowLeft, Settings, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogContentWithoutClose,
  DialogTitle,
  DialogHeader,
  DialogFooter,
} from '../../../components/ui/dialog';
import { cn } from '../../../lib/utils';
import { SearchableSelect } from '../../../components/ui/SearchableSelect';
import { Notification as customNotification } from '../../../components/ui/custom-notification';
import {
  optifitResultsApi,
  type OptifitResult,
} from '../services/optifitResultsApi';
import { useUserManagementRedux } from '../../admin/hooks/useUserManagementRedux';
import { formatTimeForDisplay } from '../../../lib/formatUtils';
import type { DashboardProject } from '../../project/types/project';
import SeamCircleSvg from './SeamCircleSvg';
import OptifitJointPipeSvg from './OptifitJointPipeSvg';

interface OptifitResultsProps {
  project: DashboardProject;
  selectedPipe?: { pipeId: string | number } | null;
}

/** Numeric values: 2 decimal places only when fractional (0.3 → 0.30); integers unchanged (960, -93). */
const formatNumericDisplay = (n: number) => {
  const rounded = Math.round(n * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
};

const formatVal = (v: unknown, unit = '') => {
  if (v === null || v === undefined) return '-';
  const n = Number(v);
  if (typeof v === 'number' || (typeof v === 'string' && v.trim() !== '' && Number.isFinite(n))) {
    return `${formatNumericDisplay(n)}${unit}`;
  }
  return `${v}${unit}`;
};

// Same date/time format as rest of app (timezone-aware: DD-MMM-YYYY, H:MM AM/PM)
const formatTimestamp = (ts: string | number | null | undefined) => {
  if (ts === null || ts === undefined || ts === '') return '-';
  try {
    const raw = String(ts).trim();
    const n = Number(raw);
    if (Number.isFinite(n) && !raw.includes('-') && !raw.includes('T')) {
      const ms = n > 1e12 ? n : n * 1000;
      return formatTimeForDisplay(ms);
    }
    return formatTimeForDisplay(ts);
  } catch {
    return '-';
  }
};

const FULLSCREEN_MODAL_SHELL = cn(
  'left-[50%] top-[50%] z-50 flex flex-col',
  'w-[min(1120px,calc(100vw-3rem))] max-w-none min-w-0',
  'h-[min(880px,calc(100dvh-3rem))] max-h-none',
  'translate-x-[-50%] translate-y-[-50%]',
  'rounded-xl border border-[#E2E5EA] p-0 gap-0',
  'bg-white shadow-2xl overflow-hidden'
);

function ConfigDetailField({ label, value }: { label: string; value: unknown }) {
  return (
    <div className="flex flex-col min-w-0">
      <span className="text-xs text-gray-500">{label}</span>
      <span className="text-sm text-gray-900 mt-0.5 break-words">{formatVal(value)}</span>
    </div>
  );
}

const getOptifitResultContextSubtitle = (viewItem: OptifitResult): string => {
  const pd = viewItem.optifit_configuration?.pipe_detail;
  const parts: (string | null)[] = [
    `Result #${viewItem.optifit_result_id}`,
    viewItem.project_num ? `Project ${viewItem.project_num}` : null,
    (() => {
      if (pd?.pipe_name != null && pd?.pipe_size != null && pd?.pipe_wall_thickness != null) {
        return `${pd.pipe_name} (${formatVal(pd.pipe_size)} x ${formatVal(pd.pipe_wall_thickness)})`;
      }
      if (pd?.pipe_size != null && viewItem.thickness != null) {
        return `${formatVal(pd.pipe_size)} x ${formatVal(viewItem.thickness, ' mm')}`;
      }
      if (pd?.pipe_size != null) return `Size ${formatVal(pd.pipe_size)}`;
      if (viewItem.thickness != null) return `Wall ${formatVal(viewItem.thickness, ' mm')}`;
      return null;
    })(),
    viewItem.pipe_length != null ? `Length ${formatVal(viewItem.pipe_length, ' m')}` : null,
  ];
  return parts.filter(Boolean).join(' - ');
};

/** Shown in table & details: Data360 `first_name` + `last_name` when present, else `operator_display_name`. */
const getOptifitResultOperatorDisplay = (result: OptifitResult): string => {
  const u = (result as OptifitResult & { data360_user?: { first_name?: string; last_name?: string } }).data360_user;
  if (u) {
    const fn = (u.first_name ?? '').toString().trim();
    const ln = (u.last_name ?? '').toString().trim();
    const full = [fn, ln].filter(Boolean).join(' ').trim();
    if (full) return full;
  }
  const op = (result.operator_display_name ?? '').toString().trim();
  if (op) return op;
  return '-';
};

export const OptifitResults: React.FC<OptifitResultsProps> = ({ project, selectedPipe }) => {
  const projectNum = project?.project?.projectNumber ?? '';
  const pipeId = selectedPipe?.pipeId;

  const [results, setResults] = useState<OptifitResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [viewItem, setViewItem] = useState<OptifitResult | null>(null);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignUserId, setAssignUserId] = useState<string>('');
  const [assignLoading, setAssignLoading] = useState(false);

  const { users, loadUsers } = useUserManagementRedux();

  useEffect(() => {
    loadUsers({});
  }, [loadUsers]);

  const fetchResults = useCallback(async () => {
    setLoading(true);
    setSelectedIds([]);
    try {
      const data = await optifitResultsApi.list({
        pipe_id: pipeId,
        project_num: projectNum || undefined,
      });
      setResults(data);
    } catch {
      customNotification.error('Error', 'Failed to load Optifit results.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [pipeId, projectNum]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  const filtered = useMemo(() => {
    const sorted = [...results].sort((a, b) => {
      const ta = a.created_at ? new Date(a.created_at).getTime() : a.optifit_result_id;
      const tb = b.created_at ? new Date(b.created_at).getTime() : b.optifit_result_id;
      return ta - tb;
    });
    if (!searchQuery.trim()) return sorted;
    const s = searchQuery.toLowerCase();
    return sorted.filter((r) => {
      const opLine = getOptifitResultOperatorDisplay(r).toLowerCase();
      const du = (r as OptifitResult & { data360_user?: { first_name?: string; last_name?: string; email?: string } }).data360_user;
      const duBits = [du?.first_name, du?.last_name, du?.email]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return (
        (r.fixed_pipe_name || '').toLowerCase().includes(s) ||
        (r.free_pipe_name || '').toLowerCase().includes(s) ||
        (r.operator_display_name || '').toLowerCase().includes(s) ||
        opLine.includes(s) ||
        duBits.includes(s) ||
        (r.device_id || '').toLowerCase().includes(s) ||
        (r.project_num || '').toLowerCase().includes(s) ||
        String(r.optifit_result_id).includes(s)
      );
    });
  }, [results, searchQuery]);

  const allSelected = filtered.length > 0 && selectedIds.length === filtered.length;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filtered.map((r) => r.optifit_result_id));
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleAssign = async () => {
    if (!assignUserId) {
      customNotification.warning('Select user', 'Please select a user to assign.');
      return;
    }
    setAssignLoading(true);
    try {
      await optifitResultsApi.assignByResults({
        result_ids: selectedIds,
        data360_user_id: Number(assignUserId),
      });
      customNotification.success('Attributed', 'Selected results have been assigned successfully.');
      setAssignModalOpen(false);
      setSelectedIds([]);
      setAssignUserId('');
      fetchResults();
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.data ||
        err?.message ||
        'Failed to assign attribution.';
      customNotification.error('Assignment failed', msg);
    } finally {
      setAssignLoading(false);
    }
  };

  const userOptions = useMemo(
    () =>
      users
        .filter((u) => u.id)
        .map((u) => ({ id: u.id, name: u.email || u.name || u.id })),
    [users]
  );

  const viewDetailIndex = useMemo(() => {
    if (!viewItem) return -1;
    return filtered.findIndex((r) => r.optifit_result_id === viewItem.optifit_result_id);
  }, [viewItem, filtered]);

  const goPrevDetail = useCallback(() => {
    if (viewDetailIndex <= 0) return;
    setViewItem(filtered[viewDetailIndex - 1]);
  }, [filtered, viewDetailIndex]);

  const goNextDetail = useCallback(() => {
    if (viewDetailIndex < 0 || viewDetailIndex >= filtered.length - 1) return;
    setViewItem(filtered[viewDetailIndex + 1]);
  }, [filtered, viewDetailIndex]);

  const prevDisabled = viewDetailIndex <= 0;
  const nextDisabled = viewDetailIndex < 0 || viewDetailIndex >= filtered.length - 1;

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Optifit Results</h3>
        {selectedIds.length > 0 && (
          <Button
            size="sm"
            className="flex items-center gap-2 bg-[#e04530] hover:bg-[#c73d2a] text-white"
            onClick={() => setAssignModalOpen(true)}
          >
            <UserCheck className="h-4 w-4" />
            Assign Attribution ({selectedIds.length})
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="mb-4 bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3 h-8">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <span className="font-medium text-sm">Filters</span>
          </div>
          {searchQuery && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSearchQuery('')}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="h-4 w-4 mr-1" />
              Clear All
            </Button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by pipe, operator, device..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mr-3" />
          <span className="text-gray-500">Loading results...</span>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="w-10">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={toggleSelectAll}
                    aria-label="Select all"
                  />
                </TableHead>
                <TableHead>#</TableHead>
                <TableHead>Fixed Pipe</TableHead>
                <TableHead>Free Pipe</TableHead>
                <TableHead>Rec. Rotation (°)</TableHead>
                <TableHead>Rec. Hi-Lo (mm)</TableHead>
                <TableHead>Operator</TableHead>
                <TableHead>Device ID</TableHead>
                <TableHead>Scan Date</TableHead>
                <TableHead className="w-16">View</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-gray-500 py-10">
                    No results found.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((row) => (
                  <TableRow
                    key={row.optifit_result_id}
                    className={selectedIds.includes(row.optifit_result_id) ? 'bg-blue-50' : undefined}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.includes(row.optifit_result_id)}
                        onCheckedChange={() => toggleSelect(row.optifit_result_id)}
                      />
                    </TableCell>
                    <TableCell className="font-medium text-xs text-gray-500">{row.optifit_result_id}</TableCell>
                    <TableCell>
                      <div className="font-medium">{row.fixed_pipe_name || '-'}</div>
                      <div className="text-xs text-gray-500">{row.fixed_pipe_end || ''}</div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{row.free_pipe_name || '-'}</div>
                      <div className="text-xs text-gray-500">{row.free_pipe_end || ''}</div>
                    </TableCell>
                    <TableCell>{formatVal(row.recommended_rotation_angle, '°')}</TableCell>
                    <TableCell>{formatVal(row.recommended_hilo, ' mm')}</TableCell>
                    <TableCell>
                      <div>{getOptifitResultOperatorDisplay(row)}</div>
                      {row.operator_employee_id && (
                        <div className="text-xs text-gray-500">{row.operator_employee_id}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-gray-600">{row.device_id || '-'}</TableCell>
                    <TableCell className="text-xs">{formatTimestamp(row.scan_timestamp)}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => setViewItem(row)}
                        title="View details"
                      >
                        <Eye className="h-4 w-4 text-gray-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* View Details Modal – same style as Report Generation / Production weld log modal */}
      <Dialog open={!!viewItem} onOpenChange={(open) => !open && setViewItem(null)}>
        <DialogContent
          className={cn(
            'left-[50%] top-[50%] z-50 flex flex-col',
            'w-[100vw] max-w-[100vw] min-w-0',
            'h-[100dvh] max-h-[100dvh]',
            'translate-x-[-50%] translate-y-[-50%]',
            'rounded-none border-0 p-0 gap-0',
            'bg-white shadow-2xl overflow-hidden'
          )}
        >
          <div className="flex flex-col min-h-0 flex-1 relative z-20">
            <DialogHeader className="px-4 sm:px-6 py-4 border-b flex flex-row items-center justify-between bg-white relative z-10 flex-shrink-0">
              <div className="flex items-center gap-4">
                <Button
                  onClick={() => setViewItem(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  variant="outline"
                >
                  <ArrowLeft className="h-5 w-5 text-gray-500" />
                </Button>
                <div>
                  {viewItem && (
                    <>
                      <DialogTitle className="text-xl font-bold text-gray-900 leading-tight">
                        {(viewItem.optifit_configuration?.pipe_detail?.pipe_name ?? '').trim() ||
                          viewItem.fixed_pipe_name ||
                          'Optifit result'}
                      </DialogTitle>
                      <p className="text-sm text-gray-600 mt-1">
                        {[
                          `Result #${viewItem.optifit_result_id}`,
                          viewItem.project_num ? `Project ${viewItem.project_num}` : null,
                          (() => {
                            const pd = viewItem.optifit_configuration?.pipe_detail;
                            if (pd?.pipe_name != null && pd?.pipe_size != null && pd?.pipe_wall_thickness != null)
                              return `${formatVal(pd.pipe_size)} × ${formatVal(pd.pipe_wall_thickness, ' mm')}`;
                            if (pd?.pipe_size != null && viewItem.thickness != null)
                              return `${pd?.pipe_size} × ${formatVal(viewItem.thickness, ' mm')}`;
                            if (pd?.pipe_size != null) return `Size ${pd.pipe_size}`;
                            if (viewItem.thickness != null) return `Wall ${formatVal(viewItem.thickness, ' mm')}`;
                            return null;
                          })(),
                          viewItem.pipe_length != null ? `Length ${formatVal(viewItem.pipe_length, ' m')}` : null,
                        ].filter(Boolean).join(' · ')}
                      </p>
                    </>
                  )}
                </div>
              </div>
              {viewItem && (
                <Button
                  onClick={() => setConfigModalOpen(true)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  variant="outline"
                  title="View configuration"
                >
                  <Settings className="h-5 w-5 text-gray-600" />
                </Button>
              )}
            </DialogHeader>

            {viewItem && (() => {
              const detail = viewItem as OptifitResult & {
                max_hi_lo?: number | null;
                top_zone_max_hilo?: number | null;
                middle_zone_max_hilo?: number | null;
                lower_zone_max_hilo?: number | null;
                user_seam_position?: number | null;
                tdc?: number | null;
                tdc_deg?: number | null;
                seam_position?: number | null;
                djseamposition?: number | null;
                dj_seam_position?: number | null;
                djseampositionDeg?: number | null;
                dj_seam_position_deg?: number | null;
              };
              const cfg = viewItem.optifit_configuration;
              const pipeDiameter = cfg?.pipe_inner_diameter ?? 0;
              const seamCapWidth = cfg?.seam_cap_width_max_mm ?? 0;
              const seamExclusion = viewItem.seam_separation_exclusion ?? 0;
              const sixOClock = viewItem.six_oclock_exclusion ?? 0;
              const circumferenceMm = pipeDiameter > 0 ? Math.PI * pipeDiameter : 0;
              const seamMmToDeg = (mm: number | null | undefined): number | undefined => {
                if (mm == null || !Number.isFinite(Number(mm)) || circumferenceMm <= 0) return undefined;
                const v = Number(mm);
                return ((v / circumferenceMm) * 360 + 360) % 360;
              };
              const fixedDeg =
                detail.user_seam_position_deg != null && Number.isFinite(detail.user_seam_position_deg)
                  ? Number(detail.user_seam_position_deg)
                  : (seamMmToDeg(detail.user_seam_position) ?? 0);
              const fixedMm = detail.user_seam_position;
              const optimumDeg =
                detail.tdc_deg != null && Number.isFinite(detail.tdc_deg)
                  ? Number(detail.tdc_deg)
                  : (seamMmToDeg(detail.tdc) ?? 0);
              const optimumMm = detail.tdc;

              /** Datum at 0 mm: fixed gauge + footer show 0° seam exclusion (matches tablet reference). */
              const fixedSeamAtDatum =
                fixedMm != null &&
                Number.isFinite(Number(fixedMm)) &&
                Math.abs(Number(fixedMm)) < 0.01;
              const fixedCardSeamExclusionDeg = fixedSeamAtDatum ? 0 : seamExclusion;

              const fixedPipeName = [viewItem.fixed_pipe_name, viewItem.fixed_pipe_end].filter(Boolean).join('') || 'Not selected';
              const freePipeName = [viewItem.free_pipe_name, viewItem.free_pipe_end].filter(Boolean).join('') || 'Not selected';

              const topLim = viewItem.top_limit ?? cfg?.hilo_preferred_limit_top_mm ?? null;
              const midLim = viewItem.middle_limit ?? cfg?.hilo_preferred_limit_middle_mm ?? null;
              const lowLim = viewItem.lower_limit ?? cfg?.hilo_preferred_limit_lower_mm ?? null;
              const hiloLimitsLine = [topLim, midLim, lowLim].every((v) => v == null || v === undefined)
                ? '-'
                : [topLim, midLim, lowLim]
                    .map((v) => (v == null || v === undefined ? '-' : formatVal(v)))
                    .join(' / ') + ' mm';

              const fmtMm = (v: number | null | undefined) => formatVal(v, ' mm');

              const hiloCards = [
                { label: 'Overall Max Hilo (mm)', value: fmtMm(detail.max_hi_lo), highlight: true },
                { label: 'Top (mm)', value: fmtMm(detail.top_zone_max_hilo), highlight: false },
                { label: 'Middle Zone (mm)', value: fmtMm(detail.middle_zone_max_hilo), highlight: false },
                { label: 'Lower Zone (mm)', value: fmtMm(detail.lower_zone_max_hilo), highlight: false },
              ];

              const metaRowsAll: [string, string][] = [
                ['Operator', formatVal(getOptifitResultOperatorDisplay(viewItem))],
                ['Optifit run timestamp', formatTimestamp(viewItem.optifit_run_timestamp)],
                // ['Pipe ID', formatVal(viewItem.pipe_id)],
                // ['Thickness', formatVal(viewItem.thickness, ' mm')],
                // ['Pipe Length', formatVal(viewItem.pipe_length, ' m')],
                // ['Rec. Rotation Angle', formatVal(viewItem.recommended_rotation_angle, '°')],
                // ['Rec. Hi-Lo', formatVal(viewItem.recommended_hilo, ' mm')],
                // ['Actual Rotation Applied', formatVal(viewItem.actual_rotation_applied, '°')],
                // ['Employee ID', formatVal(viewItem.operator_employee_id)],
                // ['Operator Type', formatVal(viewItem.operator_type)],
                // ['Attribution Status', formatVal(viewItem.attribution_status)],
                // ['Data360 User ID', formatVal(viewItem.data360_user_id)],
                // ['Red Flag', viewItem.red_flag === true ? 'Yes' : 'No'],
                // ['Seam Sep. Max', formatVal(viewItem.seam_separation_max)],
                // ['Seam Sep. Exclusion', formatVal(viewItem.seam_separation_exclusion)],
                // ['Seam Position', formatVal(detail.seam_position)],
                // ['User Seam Position', formatVal(detail.user_seam_position)],
                // ['DJ Seam Position', formatVal(detail.djseamposition ?? detail.dj_seam_position)],
                // ['DJ Seam Position (deg)', formatVal(detail.djseampositionDeg ?? detail.dj_seam_position_deg, '°')],
                // ['6 o\'clock Exclusion', formatVal(viewItem.six_oclock_exclusion)],
                // ['Scan Timestamp', formatTimestamp(viewItem.scan_timestamp)],
                // ['Device ID', formatVal(viewItem.device_id)],
                // ['Created At', formatTimestamp(viewItem.created_at)],
                // ['Updated At', formatTimestamp(viewItem.updated_at)],
              ];

              const metaRows = metaRowsAll;

              const pipeTypeLabel = formatVal(cfg?.pipe_type);
              const pipeModeLabel = formatVal(cfg?.pipe_mode);

              const pipeNameChip =
                (cfg?.pipe_detail?.pipe_name ?? '').trim() ||
                (viewItem.fixed_pipe_name ?? '').trim() ||
                '—';

              return (
                <div className="overflow-y-auto flex-1 min-h-0 w-full bg-[#f1f5f9]">
                  <div className="w-full px-4 sm:px-6 pt-4 sm:pt-5 space-y-4">
                    {/* Top strip — tablet-style chips */}
                    <div className="mb-4 rounded-[14px] border border-[#E8ECF2] bg-white px-3 py-2.5 sm:px-4 flex flex-wrap gap-x-4 gap-y-2 text-[11px] sm:text-xs text-gray-800">
                      <span>
                        <span className="text-gray-500">Pipe</span>{' '}
                        <span className="font-semibold">{pipeNameChip}</span>
                      </span>
                      <span>
                        <span className="text-gray-500">Datum seam</span>{' '}
                        <span className="font-semibold tabular-nums">{formatVal(fixedMm, ' mm')}</span>
                      </span>
                      <span>
                        <span className="text-gray-500">Optimum position</span>{' '}
                        <span className="font-semibold tabular-nums">{formatVal(optimumMm, ' mm')}</span>
                      </span>
                      <span>
                        <span className="text-gray-500">Type</span>{' '}
                        <span className="font-semibold">{pipeTypeLabel}</span>
                      </span>
                      <span>
                        <span className="text-gray-500">Mode</span>{' '}
                        <span className="font-semibold">{pipeModeLabel}</span>
                      </span>
                      <span className="min-w-0 sm:max-w-none">
                        <span className="text-gray-500">HiLo limits (Top / Mid / Low)</span>{' '}
                        <span className="font-semibold tabular-nums">{hiloLimitsLine}</span>
                      </span>
                    </div>

                    {/* Fixed / free ends + Analyze — padded row above full-bleed model */}
                    <div className="flex flex-col xl:flex-row gap-3 xl:items-end xl:justify-between">
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3 min-w-0">
                        <div className="rounded-2xl border border-white bg-white px-4 py-3 shadow-sm">
                          <p className="text-[10px] font-bold tracking-wide text-gray-400 uppercase mb-1">
                            Fixed end
                          </p>
                          <p className="text-sm font-semibold text-gray-900 break-all leading-snug">{fixedPipeName}</p>
                        </div>
                        <div className="rounded-2xl border border-white bg-white px-4 py-3 shadow-sm">
                          <p className="text-[10px] font-bold tracking-wide text-gray-400 uppercase mb-1">
                            Free end
                          </p>
                          <p className="text-sm font-semibold text-gray-900 break-all leading-snug">{freePipeName}</p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        disabled
                        variant="secondary"
                        className="shrink-0 h-12 xl:h-14 rounded-2xl px-10 text-sm font-semibold bg-zinc-700 text-white opacity-70 cursor-not-allowed border-0 shadow-sm"
                      >
                        Analyze
                      </Button>
                    </div>
                  </div>

                  {/* Joint visualization — full width band, compact pipe model */}
                  <div className="w-full mb-4 border-y border-[#E5E7EB] bg-[#FAFAFA] shadow-sm px-4 sm:px-6 py-3 flex justify-center">
                    <OptifitJointPipeSvg
                      maxHiloMm={detail.max_hi_lo}
                      redFlag={viewItem.red_flag}
                      className="w-full min-w-0 rounded-xl overflow-hidden bg-white border border-[#E8ECF2]"
                      onPrev={goPrevDetail}
                      onNext={goNextDetail}
                      prevDisabled={prevDisabled}
                      nextDisabled={nextDisabled}
                    />
                  </div>

                  <div className="w-full px-4 sm:px-6 pb-6 space-y-4">
                    {/* 3-column layout – mirrors field tablet gauges */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4 items-stretch">

                      {/* LEFT – Reference / Fixed End */}
                      <div className="bg-white rounded-[22px] border border-[#E8ECF2] shadow-sm p-4 flex flex-col h-full items-start">
                        <div className="w-full mb-1 text-center">
                          <p className="text-[15px] font-bold text-gray-900 leading-tight">Fixed End Seam Position</p>
                        </div>
                        <div className="w-full mt-3 bg-[#F8FBFF] border border-[#E7EDF5] rounded-[18px] overflow-hidden flex items-center justify-center py-2">
                          <SeamCircleSvg
                            size={220}
                            seamDegree={fixedDeg}
                            seamValueMm={fixedMm}
                            referenceDegree={0}
                            seamExclusion={fixedCardSeamExclusionDeg}
                            sixOClockValue={sixOClock}
                            pipeDiameter={pipeDiameter}
                            seamCapWidth={seamCapWidth}
                            showBump={false}
                            showSeamExclusionZone
                            showSixOClockZone
                            showRotationArc={false}
                            visualVariant="tablet"
                          />
                        </div>
                        <div className="mt-3 w-full bg-[#F8FAFC] border border-[#E8ECF2] rounded-2xl px-3 py-2">
                          <p className="text-[10px] text-gray-400 text-center mb-0.5">Seam exclusion</p>
                          <p className="text-[11px] font-bold text-gray-800 text-center">
                            {formatVal(fixedCardSeamExclusionDeg, '°')}
                          </p>
                        </div>
                      </div>

                      {/* MIDDLE – Analysis Summary */}
                      <div className="bg-white rounded-[22px] border border-[#E8ECF2] shadow-sm p-4 flex flex-col h-full items-center">
                        {/* Hero section */}
                        <div className="w-full bg-[#FFFAFB] border border-[#F5DADF] rounded-[18px] px-4 py-3 flex flex-col items-center mb-3">
                          <p className="text-[10px] font-semibold tracking-[0.8px] text-[#e04530] uppercase mb-1">
                            Analysis output
                          </p>
                          <p className="text-[14px] font-bold text-gray-900 mb-1 text-center">Analysis metrics</p>
                          <p className="text-[13px] font-semibold text-gray-700 mb-1 text-center">Optimum free seam position</p>
                          <p className="text-[28px] font-bold text-gray-900 leading-none tabular-nums">
                            {formatVal(optimumMm)}
                          </p>
                          <p className="text-[11px] text-gray-400 mt-0.5">mm</p>
                          {/* <div className="mt-3 w-full bg-white border border-[#E8ECF2] rounded-2xl px-3 py-2.5 text-center">
                            <p className="text-[12px] text-gray-800 leading-snug">
                              <span className="text-gray-500">Derived seam position: </span>
                              <span className="font-bold text-gray-900 tabular-nums">
                                {formatVal(viewItem.derived_seam_position)}
                              </span>
                            </p>
                          </div> */}
                        </div>

                        {/* HiLo 2×2 grid */}
                        <div className="grid grid-cols-2 gap-2 w-full mb-3">
                          {hiloCards.map((card) => (
                            <div
                              key={card.label}
                              className={cn(
                                'rounded-2xl border px-3 py-2',
                                card.highlight
                                  ? 'bg-[#FFF7F8] border-[#FFDCE1]'
                                  : 'bg-[#F8FAFC] border-[#E8ECF2]'
                              )}
                            >
                              <p className="text-[10px] text-gray-400 mb-1">{card.label}</p>
                              <p className="text-[15px] font-bold text-gray-900">{card.value}</p>
                            </div>
                          ))}
                        </div>

                        {viewItem.red_flag === true && (
                          <div className="mt-2 w-full flex items-center gap-1.5 text-xs font-semibold text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                            <span className="w-2 h-2 rounded-full bg-red-500 inline-block shrink-0" />
                            Red Flag
                          </div>
                        )}
                        <div className="flex-1 min-h-0 w-full" aria-hidden />
                        <div className="flex gap-2 w-full pt-2 mt-auto">
                          <Button
                            type="button"
                            variant="outline"
                            className="flex-1 h-11 rounded-xl border-gray-300 bg-white font-semibold text-gray-800 hover:bg-gray-50 inline-flex items-center justify-center gap-1.5"
                            disabled={prevDisabled}
                            onClick={goPrevDetail}
                          >
                            <ChevronLeft className="h-4 w-4 shrink-0" />
                            Previous
                          </Button>
                          <Button
                            type="button"
                            className="flex-1 h-11 rounded-xl font-semibold text-white border-0 shadow-sm inline-flex items-center justify-center gap-1.5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 disabled:pointer-events-none disabled:opacity-40"
                            disabled={nextDisabled}
                            onClick={goNextDetail}
                          >
                            Next
                            <ChevronRight className="h-4 w-4 shrink-0" />
                          </Button>
                        </div>
                      </div>

                      {/* RIGHT – Optimized / Free End */}
                      <div className="bg-white rounded-[22px] border border-[#E8ECF2] shadow-sm p-4 flex flex-col h-full items-start">
                        <div className="w-full mb-1 text-center">
                          <p className="text-[15px] font-bold text-gray-900 leading-tight">Optimum Free Seam Position</p>
                        </div>
                        <div className="w-full mt-3 bg-[#F8FBFF] border border-[#E7EDF5] rounded-[18px] overflow-hidden flex items-center justify-center py-2">
                          <SeamCircleSvg
                            size={220}
                            seamDegree={optimumDeg}
                            seamValueMm={optimumMm}
                            referenceDegree={0}
                            exclusionCentreDegree={fixedDeg}
                            seamExclusion={seamExclusion}
                            sixOClockValue={sixOClock}
                            pipeDiameter={pipeDiameter}
                            seamCapWidth={seamCapWidth}
                            showBump={false}
                            showSeamExclusionZone
                            showSixOClockZone
                            showRotationArc
                            visualVariant="tablet"
                          />
                        </div>
                        <div className="mt-3 w-full grid grid-cols-2 gap-2">
                          <div className="bg-[#F8FAFC] border border-[#E8ECF2] rounded-2xl px-3 py-2">
                            <p className="text-[10px] text-gray-400 text-center mb-0.5">Seam exclusion</p>
                            <p className="text-[11px] font-bold text-gray-800 text-center">{formatVal(seamExclusion, '°')}</p>
                          </div>
                          <div className="bg-[#F8FAFC] border border-[#E8ECF2] rounded-2xl px-3 py-2">
                            <p className="text-[10px] text-gray-400 text-center mb-0.5">6 o&apos;clock exclusion</p>
                            <p className="text-[11px] font-bold text-gray-800 text-center">{formatVal(sixOClock, '°')}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {metaRows.length > 0 && (
                      <div className="bg-white rounded-[22px] border border-[#E8ECF2] shadow-sm overflow-hidden">
                        <div className="px-5 py-3 border-b border-gray-100">
                          <span className="text-sm font-semibold text-gray-700">Additional Details</span>
                        </div>
                        <div className="px-5 pb-5">
                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-1 pt-4 text-sm">
                            {metaRows.map(([label, value]) => (
                              <div key={label} className="flex flex-col py-1.5 border-b border-gray-100">
                                <span className="text-xs text-gray-500">{label}</span>
                                <span className="font-medium text-xs mt-0.5">{formatVal(value)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                  </div>
                </div>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>

      {/* Config modal – platform Configuration pattern (large overlay, back arrow only) */}
      <Dialog open={configModalOpen} onOpenChange={setConfigModalOpen}>
        <DialogContentWithoutClose className={FULLSCREEN_MODAL_SHELL}>
          <div className="flex flex-col min-h-0 flex-1">
            <DialogHeader className="px-6 sm:px-8 py-5 border-b flex flex-row items-center justify-between bg-white flex-shrink-0">
              <div className="flex items-center gap-4 min-w-0">
                <Button
                  onClick={() => setConfigModalOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors shrink-0"
                  variant="outline"
                  title="Back"
                >
                  <ArrowLeft className="h-5 w-5 text-gray-500" />
                </Button>
                <div className="min-w-0">
                  <DialogTitle className="text-xl font-bold text-gray-900">Configuration</DialogTitle>
                  {viewItem && (
                    <p className="text-sm text-gray-600 mt-1 truncate" title={getOptifitResultContextSubtitle(viewItem)}>
                      {getOptifitResultContextSubtitle(viewItem)}
                    </p>
                  )}
                </div>
              </div>
            </DialogHeader>
            <div className="overflow-y-auto flex-1 min-h-0 px-6 sm:px-8 py-6 sm:py-8">
              {viewItem?.optifit_configuration ? (
                <div className="space-y-8">
                  <section>
                    <h4 className="text-base font-bold text-gray-900 mb-5">Optifit configuration</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
                      {[
                        ['ID', viewItem.optifit_configuration.id],
                        ['Pipe ID', viewItem.optifit_configuration.pipe_id],
                        ['Thickness', viewItem.optifit_configuration.thickness],
                        ['Pipe wall thickness', viewItem.optifit_configuration.pipe_wall_thickness],
                        ['Toe seam separation min (mm)', viewItem.optifit_configuration.toe_seam_separation_min_mm],
                        ['Seam cap width max (mm)', viewItem.optifit_configuration.seam_cap_width_max_mm],
                        ['6 o\'clock exclusion (deg)', viewItem.optifit_configuration.six_oclock_exclusion_deg],
                        ['Hilo WPS limit (mm)', viewItem.optifit_configuration.hilo_wps_limit_mm],
                        ['Excessive hilo length limit (deg)', viewItem.optifit_configuration.excessive_hilo_length_limit_deg],
                        ['Hilo preferred limit top (mm)', viewItem.optifit_configuration.hilo_preferred_limit_top_mm],
                        ['Hilo preferred limit middle (mm)', viewItem.optifit_configuration.hilo_preferred_limit_middle_mm],
                        ['Hilo preferred limit lower (mm)', viewItem.optifit_configuration.hilo_preferred_limit_lower_mm],
                        ['Pipe type', viewItem.optifit_configuration.pipe_type],
                        ['Pipe mode', viewItem.optifit_configuration.pipe_mode],
                        ['Pipe sequence', viewItem.optifit_configuration.pipe_sequence],
                        ['Pipe inner diameter', viewItem.optifit_configuration.pipe_inner_diameter],
                        ['Operator ID', viewItem.optifit_configuration.operator_id],
                        ['Operator display name', viewItem.optifit_configuration.operator_display_name],
                      ].map(([label, value]) => (
                        <ConfigDetailField key={String(label)} label={String(label)} value={value} />
                      ))}
                    </div>
                  </section>
                  {viewItem.optifit_configuration.pipe_detail && (() => {
                    const pd = viewItem.optifit_configuration.pipe_detail;
                    const pipeSizeDisplay = pd.pipe_size != null && pd.pipe_wall_thickness != null
                      ? `${formatVal(pd.pipe_size)} x ${formatVal(pd.pipe_wall_thickness)}`
                      : pd.pipe_size != null ? formatVal(pd.pipe_size) : null;
                    const pipeDetailRows: [string, string | number | null | undefined][] = [
                      ['Pipe ID', pd.pipe_id],
                      ['Pipe name', pd.pipe_name],
                      ['Pipe size', pipeSizeDisplay ?? pd.pipe_size],
                      ['Pipe order', pd.pipe_order],
                      ['Pipe wall thickness', pd.pipe_wall_thickness],
                      ['Pipe length', pd.pipe_length],
                      ['Number of pipes', pd.number_of_pipes],
                      ['Number of forges', pd.number_of_forges],
                      ['Output assembly length', pd.output_assembly_length],
                    ];
                    return (
                      <section className="border-t border-gray-200 pt-8">
                        <h4 className="text-base font-bold text-gray-900 mb-5">Pipe detail</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
                          {pipeDetailRows.map(([label, value]) => (
                            <ConfigDetailField key={String(label)} label={label} value={value} />
                          ))}
                        </div>
                      </section>
                    );
                  })()}
                </div>
              ) : (
                <p className="text-sm text-gray-500 py-4">No configuration data for this result.</p>
              )}
            </div>
          </div>
        </DialogContentWithoutClose>
      </Dialog>

      {/* Assign Attribution Modal – medium, centered, content and options contained */}
      <Dialog open={assignModalOpen} onOpenChange={(open) => { if (!open) { setAssignModalOpen(false); setAssignUserId(''); } }}>
        <DialogContent
          className={cn(
            'w-[95vw] min-w-[320px] max-w-[480px] max-h-[85vh] p-0 gap-0 border bg-white shadow-2xl overflow-hidden flex flex-col',
            'fixed left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] z-50'
          )}
        >
          <div className="flex flex-col min-h-0 flex-1 relative z-20">
            <DialogHeader className="px-4 sm:px-6 py-4 border-b flex flex-row items-center justify-between bg-white relative z-10 flex-shrink-0">
              <div className="flex items-center gap-4">
                <Button
                  onClick={() => { setAssignModalOpen(false); setAssignUserId(''); }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  variant="outline"
                >
                  <ArrowLeft className="h-5 w-5 text-gray-500" />
                </Button>
                <div>
                  <DialogTitle className="text-lg font-bold text-gray-900">
                    Assign Attribution
                    {assignLoading && (
                      <span className="ml-2 text-sm font-normal text-red-600">(Assigning...)</span>
                    )}
                  </DialogTitle>
                  <p className="text-sm text-gray-600 mt-1">
                    Assign <strong>{selectedIds.length}</strong> selected result{selectedIds.length !== 1 ? 's' : ''} to a Data360 user.
                  </p>
                </div>
              </div>
            </DialogHeader>

            <div className="p-4 sm:p-6 lg:p-8 overflow-y-auto flex-1 min-h-0">
              <div className="space-y-6">
                <div className="space-y-3">
                  <label className="text-sm font-medium text-gray-700">Select User (Email) *</label>
                  <SearchableSelect
                    value={assignUserId}
                    onValueChange={(v) => setAssignUserId(v)}
                    placeholder="Select user email"
                    searchPlaceholder="Search users..."
                    options={userOptions.map((u) => ({ id: String(u.id), name: u.name }))}
                    showAddButton={false}
                    disabled={assignLoading}
                  />
                  {assignUserId && (
                    <p className="text-xs text-gray-500">
                      Selected user ID: <strong>{assignUserId}</strong>
                    </p>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 pt-6 border-t">
                  <Button
                    variant="outline"
                    onClick={() => { setAssignModalOpen(false); setAssignUserId(''); }}
                    disabled={assignLoading}
                    className="px-6"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAssign}
                    disabled={assignLoading || !assignUserId}
                    className="bg-[#e04530] hover:bg-[#c73d2a] text-white px-6"
                  >
                    {assignLoading ? 'Assigning...' : 'Assign'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
